import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Eye, EyeOff } from "lucide-react";
import { DSPData, EditDSPData } from "../types/types";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (dspData: EditDSPData) => Promise<void>;
  // userId?: string;
  dspData?: DSPData;
}

const EditDSPModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  dspData,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  useEffect(() => {
    if (isOpen && dspData) {
      formik.setValues({
        userName: dspData.name,
        email: dspData.email,
        password: "",
      });
    }
  }, [isOpen, dspData]);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const formik = useFormik({
    initialValues: {
      userName: dspData ? dspData.name : "",
      email: dspData ? dspData.email : "",
      password: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.userName) errors.userName = "Name is required";
      if (!values.email) errors.email = "Email is required";
      return errors;
    },
    onSubmit: async (values) => {
      if (!dspData) return;

      setIsUpdating(true);

      try {
        await onSubmit({
          name: values.userName,
          email: values.email,
          password: values.password || "",
          type: dspData.type,
        });
        onClose();
      } catch (error) {
        console.error("Error in onSubmit:", error);
      } finally {
        setIsUpdating(false);
      }
    },
    enableReinitialize: true,
  });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-1/3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold mb-4">Edit DSP Details</h2>
        <form onSubmit={formik.handleSubmit}>
          <input
            type="text"
            name="userName"
            placeholder="Enter your name"
            value={formik.values.userName}
            onChange={formik.handleChange}
            className="w-full p-3 border rounded-lg mb-2 focus:ring-2 focus:ring-blue-300"
          />
          {formik.errors.userName && (
            <p className="text-red-500 text-sm">{formik.errors.userName}</p>
          )}

          <input
            type="email"
            name="email"
            placeholder="Enter your email"
            value={formik.values.email}
            onChange={formik.handleChange}
            className="w-full p-3 border rounded-lg mb-2 focus:ring-2 focus:ring-blue-300"
          />
          {formik.errors.email && (
            <p className="text-red-500 text-sm">{formik.errors.email}</p>
          )}

          <div className="relative w-full mb-2">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter new password "
              value={formik.values.password}
              onChange={formik.handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span
              className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
          </div>

          <button
            type="submit"
            disabled={isUpdating}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                Updating...
              </>
            ) : (
              "Update"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditDSPModal;
